import { createHandler as createBaseHandler } from "@webiny/handler";
import { registerDefaultPlugins } from "@webiny/handler-aws/plugins/index.js";
import { execute } from "@webiny/handler-aws/execute.js";
import type {
    APIGatewayProxyResult,
    Context as LambdaContext
} from "@webiny/aws-sdk/types/index.js";
import type { Context } from "~/api/types.js";
import type { HandlerParams, ITaskRawEvent } from "~/api/handler/types.js";
import { TaskRunner } from "~/api/runner/index.js";
import type WebinyError from "@webiny/error";
import { timerFactory } from "@webiny/handler-aws/utils/index.js";
import { TaskEventValidation } from "~/api/runner/TaskEventValidation.js";
import { TaskResultStatus } from "@webiny/api-core/features/task/TaskDefinition/index.js";

export interface HandlerCallable {
    (event: ITaskRawEvent, context: LambdaContext): Promise<APIGatewayProxyResult>;
}

const url = "/webiny-background-task-event";

export const createHandler = (params: HandlerParams): HandlerCallable => {
    return async (event, context) => {
        const app = createBaseHandler({
            ...params,
            options: {
                logger: params.debug === true,
                ...(params.options || {})
            }
        });

        registerDefaultPlugins(app.webiny);

        app.addHook("preSerialization", async (_, __, payload: Record<string, any>) => {
            if (!payload.body) {
                return payload;
            }
            return payload.body;
        });

        await app.register(async taskApp => {
            taskApp.setErrorHandler<WebinyError>(async (error, _, reply) => {
                app.__webiny_raw_result = {
                    error: {
                        message: error.message,
                        code: error.code,
                        data: error.data
                    },
                    status: TaskResultStatus.ERROR
                };
                return reply.send();
            });

            taskApp.post(url, async (_, reply) => {
                const handler = new TaskRunner(
                    /**
                     * We can safely cast because we know that the context is of type tasks/Context
                     */
                    app.webiny as Context,
                    timerFactory(context),
                    new TaskEventValidation()
                );

                app.__webiny_raw_result = await handler.run(event);
                return reply.send({});
            });
        });

        return execute({
            app,
            url,
            payload: {
                ...event,
                headers: {
                    ["x-tenant"]: event.tenant,
                    ["x-webiny-cms-endpoint"]: event.endpoint,
                    ["accept-language"]: "en-US"
                }
            }
        });
    };
};
